import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { dropShellHead } from "@/lib/shellHead";
import { cldSquare } from "@/lib/img";

// -----------------------------------------------------------------------------
// Canonical base — baked in at build time via REACT_APP_SITE_URL, falls back to
// REACT_APP_BACKEND_URL, finally to window.location.origin at runtime. This
// ensures the prerendered HTML has the RIGHT canonical (not the local prerender
// server address) once Playwright captures the DOM.
// -----------------------------------------------------------------------------
const RUNTIME_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const CANONICAL_BASE = (
  process.env.REACT_APP_SITE_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  RUNTIME_ORIGIN ||
  ""
).replace(/\/+$/, "");

const BRAND = {
  name: "VVS Lab Jewelry",
  legalName: "VVS Lab Jewellery",
  logo: `${CANONICAL_BASE}/logo512.png`,
  logo192: `${CANONICAL_BASE}/logo192.png`,
  slogan: "Trusted manufacturer & exporter of premium Gems & Jewellery since 1989.",
  founded: "1989",
  phone_ind: "+91-97993-48600",
  phone_usa: "+1-208-926-3790",
  email: "info@vvslabjewelry.com",
  address: {
    street: "Office No. 105, Sky Tower, Kotra",
    city: "Ajmer",
    region: "Rajasthan",
    postalCode: "305001",
    country: "IN",
  },
};

const abs = (u) => (!u ? u : /^https?:\/\//i.test(u) ? u : `${CANONICAL_BASE}${u.startsWith("/") ? "" : "/"}${u}`);

// No longer used in schema — shipping/returns are managed at the Merchant
// Center account level and are intentionally absent from Product/Offer JSON-LD.
// Kept only as the reference list mirroring SHIP_COUNTRIES in
// backend/routes/merchant_feed.py.
export const MERCHANT_COUNTRIES = ["US"];

// Google indexes the image URL it actually sees in the rendered markup. Our
// grids/galleries render the Bunny-optimised 1200px square variant, so every
// schema / OG / sitemap reference must use that exact same URL — otherwise
// Google sees two unrelated images per product and picks no SERP thumbnail.
const seoImg = (u) => (!u ? u : abs(cldSquare(u, 1200)));

// Normalize any date/ISO string to a full ISO-8601 with timezone so Google's
// Rich Results checker stops warning "invalid datetime / missing time zone".
// Handles: full ISO ("2026-07-24T12:34:56+00:00"), date-only ("2026-07-24"),
// or Date object. Falls back to now() when unparseable.
const isoWithTz = (value) => {
  if (!value) return new Date().toISOString();
  const s = typeof value === "string" ? value : "";
  // Already has a T + timezone offset (Z or ±HH:MM) — keep as-is.
  if (s && /T\d{2}:\d{2}/.test(s) && /(Z|[+-]\d{2}:?\d{2})$/.test(s)) return s;
  // Date-only "YYYY-MM-DD" — anchor to midday UTC so timezone shifts don't
  // roll the date backwards for readers west of UTC.
  if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T12:00:00+00:00`;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
};

// =============================================================================
// <SEO/> component
// Injects <title>, meta, canonical, OG, Twitter, and any number of JSON-LD
// blocks into the <head>. react-helmet-async serializes into the DOM before
// Playwright captures the postbuild static HTML — so every schema below ships
// pre-rendered.
// =============================================================================
export const SEO = ({ title, description, image, jsonLd, keywords, noindex = false, canonical: canonicalOverride }) => {
  const { pathname } = useLocation();
  // An explicit override lets a duplicate/thin page point at the URL that
  // should rank (e.g. /mens-watch → /moissanite-watches). Accepts an absolute
  // URL or a site-relative path.
  const selfUrl = `${CANONICAL_BASE}${pathname.toLowerCase().replace(/\/+$/, "") || "/"}`;
  const canonical = canonicalOverride
    ? (/^https?:\/\//i.test(canonicalOverride)
        ? canonicalOverride.replace(/\/+$/, "")
        : `${CANONICAL_BASE}/${canonicalOverride.replace(/^\/+|\/+$/g, "")}`)
    : selfUrl;
  const ogImage = seoImg(image);
  // Only advertise exact pixel dims when the URL is one of our own square
  // CDN transforms — third-party images have unknown dimensions.
  const ogSquare = !!ogImage && (ogImage.includes("aspect_ratio=1:1") || ogImage.includes("ar_1:1"));
  const robots = noindex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  // The downloaded HTML (static shell or a prerendered page) already carries a
  // title, canonical, description, OG/Twitter tags and JSON-LD. React 19 hoists
  // this component's versions in alongside them, so bots saw two of each — drop
  // the pre-React copies now that ours are live.
  useEffect(() => {
    dropShellHead({ keepTitle: !title });
  }, [title]);

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND.name} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:secure_url" content={ogImage} />}
      {ogImage && ogSquare && <meta property="og:image:width" content="1200" />}
      {ogImage && ogSquare && <meta property="og:image:height" content="1200" />}
      {ogImage && title && <meta property="og:image:alt" content={title} />}
      <meta property="og:url" content={canonical} />
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {ogImage && title && <meta name="twitter:image:alt" content={title} />}
      {jsonLd &&
        (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
          .filter(Boolean)
          .map((ld, i) => (
            <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
          ))}
    </Helmet>
  );
};

// =============================================================================
// Home page — Organization + WebSite (Sitelinks Searchbox)
//
// The Organization schema mirrors what the admin edits under
// Admin → Homepage → Footer + Social + Branding, so a rich-results audit
// never disagrees with what the storefront actually shows.
//
// `orgLd` accepts:
//   social   — /api/site/social output (sameAs links)
//   footer   — /api/site/footer output (about_text, phones, email, address)
//   branding — /api/site/branding output (favicon / logo overrides)
// All are optional; the hardcoded defaults kick in when a field is empty.
// =============================================================================
export const orgLd = (social = {}, footer = {}, branding = {}) => {
  const sameAs = [social.facebook, social.instagram, social.twitter, social.youtube, social.tiktok]
    .filter(Boolean);
  const logoUrl = abs(branding.logo_url || branding.apple_touch_icon_url || "") || BRAND.logo;
  const description = (footer.about_text || "").trim() ||
    "Trusted manufacturer and exporter of premium Gems & Jewellery since 1989 — Moissanite Jewellery, Moissanite Watches, Lab-Grown Diamond Jewellery, and Natural Diamond Jewellery.";
  // Parse "Street, City, Region – PostalCode, Country" out of the free-form
  // address string when a structured address isn't stored — best-effort so
  // the JSON-LD stays valid even if admin only fills one line.
  const parseAddr = (raw) => {
    const s = (raw || "").trim();
    if (!s) return null;
    // Split on comma; last segment is usually country.
    const bits = s.split(",").map((b) => b.trim()).filter(Boolean);
    if (bits.length < 2) return { streetAddress: s };
    const country = bits[bits.length - 1];
    const regionMatch = bits.length >= 2 ? bits[bits.length - 2] : "";
    // Try to pull PIN / ZIP out of the region segment
    const zipMatch = regionMatch.match(/(\d{4,10})/);
    return {
      streetAddress: bits.slice(0, Math.max(0, bits.length - 2)).join(", ") || bits[0],
      addressRegion: regionMatch.replace(/\d{4,10}/, "").replace(/[–—-]/g, "").trim(),
      postalCode: zipMatch ? zipMatch[1] : "",
      addressCountry: country,
    };
  };
  const parsedAddr = parseAddr(footer.address);
  const address = parsedAddr
    ? {
        "@type": "PostalAddress",
        ...(parsedAddr.streetAddress ? { streetAddress: parsedAddr.streetAddress } : {}),
        addressLocality: BRAND.address.city,
        ...(parsedAddr.addressRegion ? { addressRegion: parsedAddr.addressRegion } : { addressRegion: BRAND.address.region }),
        ...(parsedAddr.postalCode ? { postalCode: parsedAddr.postalCode } : { postalCode: BRAND.address.postalCode }),
        ...(parsedAddr.addressCountry ? { addressCountry: parsedAddr.addressCountry } : { addressCountry: BRAND.address.country }),
      }
    : {
        "@type": "PostalAddress",
        streetAddress: BRAND.address.street,
        addressLocality: BRAND.address.city,
        addressRegion: BRAND.address.region,
        postalCode: BRAND.address.postalCode,
        addressCountry: BRAND.address.country,
      };
  const emailForSchema = (footer.email || BRAND.email).trim();
  const phoneInd = (footer.phone_ind || BRAND.phone_ind).replace(/\s+/g, "");
  const phoneUsa = (footer.phone_usa || BRAND.phone_usa).replace(/\s+/g, "");
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${CANONICAL_BASE}/#organization`,
    name: BRAND.name,
    legalName: BRAND.legalName,
    foundingDate: BRAND.founded,
    url: CANONICAL_BASE || "/",
    logo: { "@type": "ImageObject", url: logoUrl, width: 512, height: 512 },
    image: logoUrl,
    slogan: BRAND.slogan,
    description,
    ...(sameAs.length ? { sameAs } : {}),
    address,
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        telephone: phoneInd,
        email: emailForSchema,
        areaServed: "IN",
        availableLanguage: ["English", "Hindi"],
      },
      {
        "@type": "ContactPoint",
        contactType: "sales",
        telephone: phoneUsa,
        email: emailForSchema,
        areaServed: "US",
        availableLanguage: ["English"],
      },
    ],
  };
};

export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${CANONICAL_BASE}/#website`,
  url: CANONICAL_BASE || "/",
  name: BRAND.name,
  publisher: { "@id": `${CANONICAL_BASE}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${CANONICAL_BASE}/search?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

// =============================================================================
// Breadcrumbs — used on category, product, blog pages
// =============================================================================
export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${CANONICAL_BASE}${it.path}`,
  })),
});

// =============================================================================
// Category page — CollectionPage + ItemList
// =============================================================================
export const collectionPageLd = (category, products = []) => {
  const url = `${CANONICAL_BASE}/${category.slug}`;
  // Google can serve a small image gallery next to a CollectionPage result
  // when the schema exposes multiple ImageObjects. We emit up to 3 — the
  // category hero (if any) first, then the top product photos.
  const galleryUrls = [];
  if (category.image) galleryUrls.push(seoImg(category.image));
  for (const p of products) {
    if (galleryUrls.length >= 6) break;
    const src = p.images && p.images[0];
    if (src) {
      const url = seoImg(src);
      if (!galleryUrls.includes(url)) galleryUrls.push(url);
    }
  }
  const imageObjects = galleryUrls.map((u) => ({
    "@type": "ImageObject",
    url: u,
    contentUrl: u,
  }));
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${url}#collection`,
    url,
    name: category.meta_title || category.name,
    description: category.meta_description || category.description || `Shop ${category.name} at ${BRAND.name}.`,
    isPartOf: { "@id": `${CANONICAL_BASE}/#website` },
    inLanguage: "en-US",
    ...(galleryUrls.length ? { primaryImageOfPage: { "@type": "ImageObject", url: galleryUrls[0], contentUrl: galleryUrls[0] } } : {}),
    ...(imageObjects.length ? { image: imageObjects } : {}),
    numberOfItems: products.length,
  };
};

export const itemListLd = (products = [], categorySlug = "") => {
  // Emit each ListItem as a full Product entity so Google can build a real
  // Product carousel result off the category page. Each Product includes
  // brand + description + sku (satisfies "global identifier"). Shipping and
  // returns are deliberately omitted — they're managed globally at the
  // Merchant Center account level (same as productLd).
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    numberOfItems: products.length,
    itemListElement: products.map((p, i) => {
      const url = `${CANONICAL_BASE}/${p.category_slug || categorySlug}/${p.slug}`;
      const src = p.images && p.images[0] ? seoImg(p.images[0]) : null;
      const imageObj = src
        ? {
            "@type": "ImageObject",
            url: src,
            contentUrl: src,
            caption: p.name,
          }
        : null;
      // Google Merchant caps sku/mpn at 50 chars — same short-id rule as productLd.
      const shortId = (p.sku && String(p.sku).trim()) || (p.id ? `VVS-${String(p.id).replace(/-/g, "").slice(0, 12).toUpperCase()}` : String(p.slug || "").slice(0, 50));
      const item = {
        "@type": "Product",
        "@id": `${url}#product`,
        url,
        name: p.name,
        description: p.description || p.meta_description || `${p.name} — ${BRAND.name}`,
        brand: { "@type": "Brand", name: BRAND.name },
        sku: shortId,
        mpn: shortId,
        category: p.category_slug || categorySlug,
        ...(imageObj ? { image: imageObj } : {}),
        ...(p.rating_count && p.rating_count > 0
          ? {
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: Number(p.rating_avg).toFixed(2),
                reviewCount: p.rating_count,
                bestRating: 5,
                worstRating: 1,
              },
            }
          : {}),
        ...(p.price != null
          ? {
              offers: {
                "@type": "Offer",
                url,
                priceCurrency: "USD",
                price: Number(p.price || 0).toFixed(2),
                priceValidUntil: new Date(new Date().getFullYear() + 1, 11, 31).toISOString().slice(0, 10),
                availability: p.in_stock === false
                  ? "https://schema.org/OutOfStock"
                  : "https://schema.org/InStock",
                itemCondition: "https://schema.org/NewCondition",
                seller: { "@type": "Organization", name: BRAND.name },
              },
            }
          : {}),
      };
      return {
        "@type": "ListItem",
        position: i + 1,
        item,
      };
    }),
  };
};

// =============================================================================
// Product page — Product + Offer + AggregateRating + Review[]
// =============================================================================
export const productLd = (p) => {
  const url = `${CANONICAL_BASE}/${p.category_slug}/${p.slug}`;
  // Google Merchant enforces sku/mpn max length 50. Prefer the merchant SKU,
  // else fall back to short UUID prefix, never the full slug (which can be 60+).
  const shortId = (p.sku && String(p.sku).trim()) || (p.id ? `VVS-${String(p.id).replace(/-/g, "").slice(0, 12).toUpperCase()}` : String(p.slug || "").slice(0, 50));
  const ld = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${url}#product`,
    name: p.name,
    description: p.description || p.meta_description || `${p.name} — ${BRAND.name}`,
    image: (p.images || []).map(seoImg),
    sku: shortId,
    mpn: shortId,
    brand: { "@type": "Brand", name: BRAND.name },
    category: p.category_slug,
    url,
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: "USD",
      price: Number(p.price || 0).toFixed(2),
      priceValidUntil: new Date(new Date().getFullYear() + 1, 11, 31).toISOString().slice(0, 10),
      availability: p.in_stock === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: BRAND.name },
      // hasMerchantReturnPolicy + shippingDetails intentionally omitted —
      // shipping/returns are managed globally at the Merchant Center account
      // level, and hardcoding US here conflicts with that setup.
    },
  };
  if (p.reviews_summary && p.reviews_summary.count > 0) {
    ld.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: p.reviews_summary.average,
      reviewCount: p.reviews_summary.count,
      bestRating: 5,
      worstRating: 1,
    };
  }
  if (Array.isArray(p.reviews) && p.reviews.length > 0) {
    ld.review = p.reviews.map((r) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: r.rating,
        bestRating: 5,
        worstRating: 1,
      },
      author: { "@type": "Person", name: r.author_name || r.name || "Verified customer" },
      ...(r.created_at ? { datePublished: isoWithTz(r.created_at) } : {}),
      reviewBody: r.body || r.text || r.comment || "",
    }));
  }
  return ld;
};

// =============================================================================
// Blog post — BlogPosting + Author (E-E-A-T)
// =============================================================================
const wordsIn = (text = "") => text.replace(/[^\w\s]+/g, " ").split(/\s+/).filter(Boolean).length;

export const blogPostingLd = (post) => {
  const url = `${CANONICAL_BASE}/blog/${post.slug}`;
  const wordCount = wordsIn(post.content || "");
  const published = isoWithTz(post.published_at || post.created_at || new Date().toISOString());
  const modified = isoWithTz(post.updated_at || post.published_at || post.created_at || published);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    headline: post.title,
    description: post.excerpt || post.meta_description || post.title,
    image: post.cover_image ? [seoImg(post.cover_image)] : undefined,
    datePublished: published,
    dateModified: modified,
    wordCount,
    inLanguage: "en-US",
    isPartOf: { "@id": `${CANONICAL_BASE}/#website` },
    publisher: { "@id": `${CANONICAL_BASE}/#organization` },
    author: {
      "@type": "Person",
      name: post.author_name || "Vinay Rajani",
      jobTitle: post.author_title || "Founder, VVS Lab Jewelry — Master Jeweler & Hip Hop Jewelry Expert",
      worksFor: { "@id": `${CANONICAL_BASE}/#organization` },
      url: `${CANONICAL_BASE}/about`,
    },
  };
};

// =============================================================================
// FAQ page (used on category pages + FAQs page)
// =============================================================================
export const faqLd = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});
